import useFormat from "hooks/useFormat";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import ColorLegend from "../charts/lib/ColorLegend";
import { ChartGrid } from "../charts-motion";
import { useColorScaleVDP, VerticalDotPlot } from "../charts-motion/vertical-dot-plot";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_equity_fig_GPIBYWEALTH";
import { datasets } from "../data/data_equity_fig_GPIBYWEALTH_datasets";
import { foldGeoEntity, getLevelACName, getRegionIndex, LevelAC, mkGemEntityDecoder, ordLevelAC, useNamedEntities, useRegionEntities } from "../domain";
import { useMedia } from "../hooks";
import { extentNumber, unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { Ar, identity, io, Ord } from "../prelude";
export * from "../data/data_equity_fig_GPIBYWEALTH";
const yUnitLabel = defineMessage({
    id: "fig.equity.GPIBYWEALTH.legend",
    defaultMessage: "Education levels, low to high"
});
const wealthOrder = {
    POOREST: 0,
    AVERAGE: 1,
    RICHEST: 2
};
export const Wealth = io.keyof(wealthOrder);
export const getWealthIndex = (k)=>wealthOrder[k];
export const ordWealth = Ord.contramap((Wealth)=>getWealthIndex(Wealth))(Ord.ordNumber);
const wealthLookup = {
    RICHEST: defineMessage({
        id: "fig.equity.GPIBYWEALTH.RICHEST",
        defaultMessage: "Richest"
    }),
    AVERAGE: defineMessage({
        id: "fig.equity.GPIBYWEALTH.AVERAGE",
        defaultMessage: "Average"
    }),
    POOREST: defineMessage({
        id: "fig.equity.GPIBYWEALTH.POOREST",
        defaultMessage: "Poorest"
    })
};
export const getWealthName = (intl, wealth)=>intl.formatMessage(wealthLookup[wealth]);
export const Data = mkGemEntityDecoder([], {
    value: io.number,
    variable: LevelAC,
    wealth: Wealth
});
export const Chart = ({ data: dataRaw })=>{
    const intl = useIntl();
    const env = useConfig();
    const screenSDown = !useMedia(breakpoints.s);
    const screenMDown = !useMedia(breakpoints.m);
    const entityData_ = useNamedEntities(dataRaw);
    const entityData = React.useMemo(()=>{
        const data = entityData_.map((x)=>({
                ...x,
                ind_name: getLevelACName(intl, x.variable),
                wealth_name: getWealthName(intl, x.wealth)
            }));
        return Ar.sortBy([
            // FIXME: It would be better to have an Ord instance that could sort geo entities and the like
            Ord.contramap((x)=>{
                return foldGeoEntity({
                    country: ()=>-Infinity,
                    region: ({ id })=>getRegionIndex(env.regions, id)
                })(x);
            })(Ord.ordNumber),
            Ord.contramap((x)=>x.variable)(ordLevelAC),
            Ord.contramap((x)=>x.wealth)(ordWealth),
            Ord.contramap((x)=>x.entity_name)(Ord.ordString)
        ])(data);
    }, [
        env.regions,
        entityData_,
        intl
    ]);
    const regionData = useRegionEntities(entityData);
    const chartData = React.useMemo(()=>{
        return unsafeFromArray(regionData.filter(({ entity_type })=>entity_type === "geo"));
    }, [
        regionData
    ]);
    const getColumn = React.useCallback((x)=>x.entity_name, []);
    const getX = React.useCallback((x)=>x.ind_name, []);
    const getValue = React.useCallback((x)=>x.value, []);
    const getColor = React.useCallback((x)=>x.wealth_name, []);
    const getColorPalette = React.useCallback((count)=>M.fromCount(M.colorRanges.diverging, count), []);
    const formatValue = useFormat(".2f");
    const formatAxisValue = useFormat(".1f");
    const extentAll = React.useMemo(()=>{
        return extentNumber(unsafeFromArray(chartData), getValue);
    }, [
        chartData,
        getValue
    ]);
    const [colorScale, colorLegendValues] = useColorScaleVDP({
        data: chartData,
        getColor,
        colorPalette: getColorPalette,
        formatColor: identity
    });
    return <>
      <ChartGrid data={chartData} getCell={getColumn} columnCount={3} minWidth={80}>
        {({ data, width, firstInRow })=><VerticalDotPlot width={width} height={screenSDown ? M.chartHeight.xs : screenMDown ? M.chartHeight.s : M.chartHeight.m} data={data} getX={getX} getValue={getValue} getColor={getColor} formatX={identity} formatValue={formatValue} formatAxisValue={formatAxisValue} domainY={extentAll} colorScale={colorScale} colorLegendValues={colorLegendValues} showTooltip labelStyleX="none" marginLeft={20} hideYAxis={!firstInRow} yUnitLabel={firstInRow ? intl.formatMessage(yUnitLabel) : null} yAnnotations={[
            {
                type: "band",
                label: intl.formatMessage(defineMessage({
                    id: "Parity",
                    defaultMessage: "Parity"
                })),
                range: [
                    0.95,
                    1.05
                ],
                dy: screenMDown ? "-0.7em" : undefined
            },
            {
                type: "line",
                value: 1
            }
        ]} dotStyle={screenSDown ? "medium" : "default"}/>}
      </ChartGrid>
      <ColorLegend inline maxWidth={"100%"} values={colorLegendValues}/>
    </>;
};
export default withFigureIO({
    url: new URL("../data/data_equity_fig_GPIBYWEALTH.json", import.meta.url).href,
    csv: new URL("../data/data_equity_fig_GPIBYWEALTH.zip", import.meta.url).href,
    xlsx: new URL("../data/data_equity_fig_GPIBYWEALTH.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    size: "wide",
    datasets
});
